"use client";
import { useState, useEffect } from "react";
import { Spin, Empty } from "antd";
import { IoChevronDown } from "react-icons/io5";
import CourseService from "@/services/api/CourseService";
import HomePageService from "@/services/api/HomeService";
import CommonService from "@/services/api/CommonService";
import CourseCard from "@/components/common/card/CourseCard";
import SectionTitle from "@/components/common/section-title/SectionTitle";
import CommonImage from "@/components/common/image/CommonImage";
import ProgramCard from "@/components/common/card/ProgramCard";
import { useParams, useRouter } from "next/navigation";
import CoursePageCard from "./components/CoursePageCard";

export default function CoursePage() {
  const params = useParams();
  const router = useRouter();
  const { programSlug } = params;

  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const programsData = await HomePageService.getPrograms();
      const allCoursesProgram = {
        name: "সকল কোর্স",
        icon_url: "/images/home-page/all-courses.avif",
        slug: "all",
        id: null,
      };
      const updatedPrograms = [allCoursesProgram, ...(programsData || [])];
      setPrograms(updatedPrograms || []);

      let initialProgram =
        updatedPrograms.find((p) => p.slug === programSlug) ||
        allCoursesProgram;

      setSelectedProgram(initialProgram);

      const classData = await CommonService.getProgamClasses(
        initialProgram?.slug
      );
      setClasses(classData || []);
      setLoading(false);
    } catch (err) {
      setLoading(false);

      console.error("Error loading initial programs/classes:", err);
    }
  };

  const fetchCourses = async (
    pageNum = 1,
    programSlugParam = null,
    classId = null
  ) => {
    try {
      setLoading(true);
      const response = await CourseService.getFilteredCourses({
        page: pageNum,
        program_slug: programSlugParam === "all" ? null : programSlugParam,
        class_id: classId,
      });

      const resData = response.courses;
      const newCourses = resData.data || [];

      if (pageNum === 1) {
        setCourses(newCourses);
      } else {
        setCourses((prev) => [...prev, ...newCourses]);
      }

      if (!resData?.next_page_url || newCourses.length === 0) {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchCourses(nextPage, selectedProgram?.slug, selectedClassId);
  };

  const handleProgramClick = (program) => {
    //   if (!program || !program.slug) {
    //   console.error('Program or program.slug is undefined');
    //   return; // অথবা উপযুক্ত fallback
    // }
    setSelectedProgram(program);
    setSelectedClassId(null);
    setPage(1);
    setHasMore(true);

    router.push(`/courses/${program.slug}`, undefined, { shallow: true });

    fetchCourses(1, program.slug, null);
  };

  const handleClassClick = (classId) => {
    setSelectedClassId(classId);
    setPage(1);
    setHasMore(true);
    fetchCourses(1, selectedProgram?.slug, classId);
  };

  useEffect(() => {
    if (!programs.length) return;

    const foundProgram =
      programs.find((p) => p.slug === programSlug) ||
      programs.find((p) => p.slug === "all");

    if (foundProgram) {
      setSelectedProgram(foundProgram);
      setSelectedClassId(null);
      setPage(1);
      setHasMore(true);
      fetchCourses(1, foundProgram.slug, null);
    }
  }, [programSlug, programs]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  return (
    <section className="course_section course-page_section section_padding">
      <div className="container">
        <div className="course-section-card">
          <SectionTitle
            iconSrc={"/images/home-page/book-icon.avif"}
            title={`${selectedProgram?.name ?? ''}`}
            description={
              "ফোকাস - যেখানে একাডেমিক সাফল্যের পথ তৈরি হয়। অভিজ্ঞ শিক্ষকদের গাইডলাইন, নিয়মিত পরীক্ষার ব্যবস্থা এবং টপিকভিত্তিক অনুশীলনের মাধ্যমে আমরা গড়ে তুলি ভবিষ্যতের চ্যাম্পিয়ন।"
            }
          />

          {/* programs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 my-8 course_prorams">
            {programs?.map((item, index) => (
              <div
                className="program-item"
                key={index}
                onClick={() => handleProgramClick(item)}
              >
                <div
                  className={`item-card ${
                    selectedProgram?.slug === item.slug ? "active" : ""
                  }`}
                >
                  <div className="program-icon">
                    <CommonImage
                      src={item.icon_url}
                      className="img-icon"
                      alt="Program Icon"
                    />
                  </div>
                  <div className="item-content">
                    <h4 className="title">{item.name}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* classes */}
          <div className="flex flex-wrap gap-3 md:gap-4 lg:gap-6 my-5 md:my-8 course_classes">
            {classes?.map((item, index) => (
              <div
                key={index}
                onClick={() => handleClassClick(item.id)}
                className={`class-item ${
                  selectedClassId === item.id ? "active" : ""
                }`}
              >
                <div className="item-content">
                  <h4 className="title">{item.title}</h4>
                </div>
              </div>
            ))}
          </div>

          {/* Courses */}
          <div className="grid xl:grid-cols-4 lg:grid-cols-3 grid-cols-2 gap-3 sm:gap-4 md:gap-6 courses">
            {loading ? (
              <div className="flex items-center justify-center h-[200px] w-full col-span-full">
                <Spin size="large" />
              </div>
            ) : courses.length > 0 ? (
              courses.map((item, index) => (
                <CourseCard key={index} course={item} />
              ))
            ) : (
              <div className="col-span-full">
                <Empty description="No course available" />
              </div>
            )}
          </div>

          {/* see more button */}
          {hasMore && (
            <div className="text-center mt-8">
              <button onClick={handleLoadMore} className="seemore-btn">
                আরো দেখো <IoChevronDown />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
